import { Component } from '@theme/component';
import '@theme/dialog';

/**
 * Behavior for the GARABATO sections. Each element extends the theme's Component base, so
 * declarative `on:` handlers and `ref` lookups work the same way as in the rest of the theme.
 */

const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

/**
 * Plays a muted, looping video. It pauses when the user prefers reduced motion. An optional
 * play/pause toggle stays in sync with the video.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} [toggle] - Optional play/pause button.
 *
 * @extends Component<Refs>
 */
class GbLoopVideoComponent extends Component {
  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    // play and pause do not bubble, so listen in the capture phase on this element.
    this.addEventListener('play', this.#syncToggle, { capture: true, signal });
    this.addEventListener('pause', this.#syncToggle, { capture: true, signal });
    reducedMotionQuery.addEventListener('change', this.#applyMotionPreference, { signal });

    if (this.refs.toggle) this.refs.toggle.hidden = false;
    this.#applyMotionPreference();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = undefined;
    super.disconnectedCallback();
  }

  /**
   * Plays the video when it is paused, and pauses it when it is playing.
   * Called by the toggle's on:click.
   */
  togglePlayback() {
    const video = this.querySelector('video');
    if (!video) return;

    if (video.paused) {
      video.play().catch(() => {});
    } else {
      video.pause();
    }
  }

  #applyMotionPreference = () => {
    const video = this.querySelector('video');
    if (!video) return;

    if (reducedMotionQuery.matches) {
      video.pause();
    } else if (video.hasAttribute('autoplay')) {
      video.play().catch(() => {});
    }

    this.#syncToggle();
  };

  #syncToggle = () => {
    const video = this.querySelector('video');
    const { toggle } = this.refs;
    if (!video || !toggle) return;

    const isPlaying = !video.paused;
    toggle.dataset.playing = String(isPlaying);
    toggle.setAttribute('aria-label', isPlaying ? toggle.dataset.labelPause : toggle.dataset.labelPlay);
  };
}

/**
 * Newsletter popup. It opens once after a delay and stays closed during the cooldown after
 * the shopper dismisses it. It stops after a signup. In the theme editor it opens only when
 * its section is selected. It sits inside the theme's dialog-component, which owns the modal
 * behavior: Escape, backdrop clicks, scroll lock, and focus return.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} [copyButton] - Copies the discount code when the clipboard is available.
 * @property {HTMLElement} [code] - Element that holds the discount code.
 * @property {HTMLElement} [copyStatus] - Live region that confirms a copy.
 *
 * @extends Component<Refs>
 */
class GbNewsletterPopupComponent extends Component {
  static DISMISSED_AT_KEY = 'gb-newsletter-popup:dismissed-at';
  static SUBSCRIBED_KEY = 'gb-newsletter-popup:subscribed';

  /** @type {AbortController | undefined} */
  #abortController;
  /** @type {number | undefined} */
  #openTimeout;

  connectedCallback() {
    super.connectedCallback();

    const dialogComponent = this.#dialogComponent;
    if (!dialogComponent) return;

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    const { copyButton } = this.refs;

    dialogComponent.addEventListener('dialog:close', this.#handleDialogClose, { signal });
    this.addEventListener('submit', this.#handleSubmit, { capture: true, signal });

    if (copyButton && navigator.clipboard) copyButton.hidden = false;

    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:section:select', this.#handleSectionSelect, { signal });
      document.addEventListener('shopify:section:deselect', this.#handleSectionDeselect, { signal });
      return;
    }

    // A signup, or a return from the customer endpoint, shows the welcome code.
    if (this.dataset.state === 'success' || this.#hasCustomerPostedParam()) {
      this.dataset.state = 'success';
      this.#storeValue(GbNewsletterPopupComponent.SUBSCRIBED_KEY, 'true');
      this.#scheduleOpen(0);
      return;
    }

    // Form errors are only visible while the dialog is open.
    if (this.dataset.errors === 'true') {
      this.#scheduleOpen(0);
      return;
    }

    if (this.#storedValue(GbNewsletterPopupComponent.SUBSCRIBED_KEY) || this.#isInCooldown()) return;

    this.#scheduleOpen(Number(this.dataset.delaySeconds || 0) * 1000);
  }

  disconnectedCallback() {
    window.clearTimeout(this.#openTimeout);
    this.#abortController?.abort();
    this.#abortController = undefined;
    super.disconnectedCallback();
  }

  /**
   * Copies the discount code. Called by the copy button's on:click.
   */
  async copyCode() {
    const { code, copyStatus } = this.refs;
    if (!code || !navigator.clipboard) return;

    try {
      await navigator.clipboard.writeText(code.textContent.trim());
      if (copyStatus) copyStatus.textContent = this.dataset.copiedLabel ?? '';
    } catch {
      // The code stays on screen, so the shopper can still copy it by hand.
    }
  }

  /**
   * The dialog-component that wraps this popup.
   *
   * @returns {HTMLElement | null}
   */
  get #dialogComponent() {
    return this.closest('dialog-component');
  }

  /**
   * Opens the dialog after a delay. A zero delay still defers the open, so the wrapping
   * dialog-component has finished connecting before it is shown.
   *
   * @param {number} delayMs
   */
  #scheduleOpen(delayMs) {
    window.clearTimeout(this.#openTimeout);
    this.#openTimeout = window.setTimeout(() => this.#dialogComponent?.showDialog?.(), delayMs);
  }

  /**
   * Shopify's bot protection can show an hCaptcha challenge on submit. A modal dialog sits in the
   * top layer, above the challenge, so the shopper could not solve it. Reopen it as a non-modal
   * dialog, which keeps the form in place but lets the challenge stack above it.
   */
  #handleSubmit = () => {
    const dialog = this.closest('dialog');
    if (!dialog?.matches(':modal')) return;

    dialog.close();
    dialog.classList.add('gb-newsletter-popup__dialog--submitting');
    dialog.show();
  };

  #handleDialogClose = () => {
    this.#storeValue(GbNewsletterPopupComponent.DISMISSED_AT_KEY, String(Date.now()));
  };

  #handleSectionSelect = (event) => {
    if (event.target instanceof HTMLElement && event.target.contains(this)) {
      this.#dialogComponent?.showDialog?.();
    }
  };

  #handleSectionDeselect = (event) => {
    if (event.target instanceof HTMLElement && event.target.contains(this)) {
      this.#dialogComponent?.closeDialog?.();
    }
  };

  #isInCooldown() {
    const dismissedAt = Number(this.#storedValue(GbNewsletterPopupComponent.DISMISSED_AT_KEY));
    if (!dismissedAt) return false;

    const cooldownMs = Number(this.dataset.cooldownDays || 0) * 24 * 60 * 60 * 1000;
    return Date.now() - dismissedAt < cooldownMs;
  }

  #hasCustomerPostedParam() {
    return new URLSearchParams(window.location.search).get('customer_posted') === 'true';
  }

  /**
   * @param {string} key
   * @returns {string | null}
   */
  #storedValue(key) {
    try {
      return window.localStorage.getItem(key);
    } catch {
      return null;
    }
  }

  /**
   * @param {string} key
   * @param {string} value
   */
  #storeValue(key, value) {
    try {
      window.localStorage.setItem(key, value);
    } catch {
      // Storage can be blocked. The popup may then show again on the next visit.
    }
  }
}

if (!customElements.get('gb-loop-video-component')) {
  customElements.define('gb-loop-video-component', GbLoopVideoComponent);
}

if (!customElements.get('gb-newsletter-popup-component')) {
  customElements.define('gb-newsletter-popup-component', GbNewsletterPopupComponent);
}
